/**
 * Tenant Config API Service (Sprint 171 D-006 / Wave C4 + Sprint C11 Lane 6)
 *
 * Type-safe client for tenant-scoped configuration endpoints. Every method
 * addresses the tenant by explicit id; the implicit-tenant (JWT context)
 * notification pair had no caller and was deleted by operator ruling
 * (2026-09-27, unwired-deletions).
 *   GET  /api/admin/tenants/{tenantId}/notifications           (explicit tenant, platform-admin)
 *   PUT  /api/admin/tenants/{tenantId}/notifications           (explicit tenant, platform-admin)
 *   GET  /api/admin/tenants/{tenantId}/sla                     (explicit tenant, platform-admin)
 *   PUT  /api/admin/tenants/{tenantId}/sla                     (explicit tenant, platform-admin)
 *   GET  /api/admin/tenants/{tenantId}/reviewers               (explicit tenant, platform-admin)
 *   PUT  /api/admin/tenants/{tenantId}/reviewers               (explicit tenant, platform-admin)
 *
 * Types derived from platform-admin.openapi.yaml via
 * `@aaimplatform/institutional-types/platform-admin`.
 *
 * Uses the shared `platformAdminClient` (anchored at `/api/admin`, already
 * auth-middlewared in ../client.ts).
 */

import { platformAdminClient } from '../typed-clients';
import type { components } from '@aaimplatform/institutional-types/platform-admin';

export type TenantReviewerRoutingConfig = components['schemas']['TenantReviewerRoutingConfig'];
export type TenantReviewerRoutingConfigUpdate = components['schemas']['TenantReviewerRoutingConfigUpdate'];
export type ReviewerAssignmentOptions = NonNullable<TenantReviewerRoutingConfig['reviewerAssignment']>;

export type TenantNotificationConfig = components['schemas']['TenantNotificationConfig'];
export type TenantNotificationChannel = components['schemas']['TenantNotificationChannel'];
export type NotificationChannelType = components['schemas']['NotificationChannelType'];
export type NotificationChannelFilter = components['schemas']['NotificationChannelFilter'];
export type WebhookChannelConfig = components['schemas']['WebhookChannelConfig'];
export type SlackChannelConfig = components['schemas']['SlackChannelConfig'];
export type TeamsChannelConfig = components['schemas']['TeamsChannelConfig'];

export type TenantSlaConfig = components['schemas']['TenantSlaConfig'];
export type TenantReviewerConfig = components['schemas']['TenantReviewerConfig'];
export type TenantReviewerAssignmentRule = components['schemas']['TenantReviewerAssignmentRule'];
export type TenantReviewerSizeThresholds = components['schemas']['TenantReviewerSizeThresholds'];

function unwrap<T>(result: {
  data?: T;
  error?: unknown;
  response: Response;
}): T {
  if (result.error || result.data === undefined) {
    const status = result.response?.status ?? 0;
    const body = result.error as { message?: string } | undefined;
    throw new Error(body?.message ?? `HTTP ${status}`);
  }
  return result.data;
}

export const tenantConfigApi = {
  /**
   * Fetch the full notification configuration for a specific tenant. Used by
   * platform administrators editing a named FI's config.
   */
  getTenantNotificationConfig: async (
    tenantId: string,
  ): Promise<TenantNotificationConfig> => {
    const result = await platformAdminClient.GET(
      '/tenants/{tenantId}/notifications',
      { params: { path: { tenantId } } },
    );
    return unwrap(result);
  },

  /**
   * Replace the full notification configuration for a specific tenant. The
   * platform-admin spec PUT is a full replace; callers MUST send the complete
   * config (all channels, digest settings, timezone, etc).
   */
  putTenantNotificationConfig: async (
    tenantId: string,
    body: TenantNotificationConfig,
  ): Promise<TenantNotificationConfig> => {
    const result = await platformAdminClient.PUT(
      '/tenants/{tenantId}/notifications',
      { params: { path: { tenantId } }, body },
    );
    return unwrap(result);
  },

  /**
   * Fetch the SLA threshold configuration for a specific tenant. Drives
   * green/yellow/red task age bands and digest scheduling.
   */
  getTenantSlaConfig: async (tenantId: string): Promise<TenantSlaConfig> => {
    const result = await platformAdminClient.GET(
      '/tenants/{tenantId}/sla',
      { params: { path: { tenantId } } },
    );
    return unwrap(result);
  },

  /**
   * Replace the tenant SLA configuration. Spec is full-replace; the server
   * validates greenHours < yellowHours.
   */
  putTenantSlaConfig: async (
    tenantId: string,
    body: TenantSlaConfig,
  ): Promise<TenantSlaConfig> => {
    const result = await platformAdminClient.PUT(
      '/tenants/{tenantId}/sla',
      { params: { path: { tenantId } }, body },
    );
    return unwrap(result);
  },

  /**
   * Fetch the reviewer assignment configuration for a specific tenant.
   * Drives pledge routing (default_only / round_robin / by_package_size).
   */
  getTenantReviewerConfig: async (
    tenantId: string,
  ): Promise<TenantReviewerConfig> => {
    const result = await platformAdminClient.GET(
      '/tenants/{tenantId}/reviewers',
      { params: { path: { tenantId } } },
    );
    return unwrap(result);
  },

  /**
   * Replace the reviewer assignment configuration. Spec is full-replace.
   * Server enforces rule-specific validation:
   *  - round_robin -> roundRobinReviewerIds non-empty
   *  - by_package_size -> sizeThresholds present and complete
   */
  putTenantReviewerConfig: async (
    tenantId: string,
    body: TenantReviewerConfig,
  ): Promise<TenantReviewerConfig> => {
    const result = await platformAdminClient.PUT(
      '/tenants/{tenantId}/reviewers',
      { params: { path: { tenantId } }, body },
    );
    return unwrap(result);
  },
};
